/**
 * DocumentImportExportManager - Professional .docx and .pdf import/export
 * 
 * Provides high-fidelity document import/export with layout preservation,
 * legal formatting retention, and professional output quality.
 */

import { LocalDocumentState, AttorneyInfo, LegalDocumentType, Jurisdiction, TextRange } from '../types/DocumentTypes';

interface ImportOptions {
  preserveFormatting: boolean;
  extractImages: boolean;
  preserveComments: boolean;
  preserveTrackChanges: boolean;
  convertToEditable: boolean;
  validateLegalContent: boolean;
}

interface ExportOptions {
  format: 'docx' | 'pdf' | 'rtf' | 'txt';
  includeMetadata: boolean;
  preserveFormatting: boolean;
  includeTrackChanges: boolean;
  includeComments: boolean;
  pdfOptions?: PDFExportOptions;
  docxOptions?: DOCXExportOptions;
}

interface PDFExportOptions {
  paperSize: 'letter' | 'legal' | 'a4';
  orientation: 'portrait' | 'landscape';
  margins: { top: number; bottom: number; left: number; right: number };
  includeBookmarks: boolean;
  protectDocument: boolean;
  password?: string;
  allowPrinting: boolean;
  allowCopying: boolean;
  watermark?: string;
}

interface DOCXExportOptions {
  compatibility: 'office365' | 'office2019' | 'office2016' | 'office2013';
  includeStyles: boolean;
  includeHeaders: boolean;
  includeFooters: boolean;
  includePageNumbers: boolean;
  protectDocument: boolean;
  trackChanges: boolean;
}

interface ImportResult {
  success: boolean;
  documentState: LocalDocumentState;
  warnings: string[];
  errors: string[];
  metadata: DocumentMetadata;
  preservedElements: string[];
  lostElements: string[];
}

interface ExportResult {
  success: boolean;
  blob: Blob;
  filename: string;
  warnings: string[];
  errors: string[];
  fileSize: number;
  exportTime: number;
}

interface DocumentMetadata {
  originalFormat: string;
  author: string;
  created: Date;
  modified: Date;
  title?: string;
  subject?: string;
  keywords?: string[];
  pageCount: number;
  wordCount: number;
  characterCount: number;
}

export class DocumentImportExportManager {
  private supportedImportFormats: string[] = ['docx', 'pdf', 'rtf', 'txt', 'html'];
  private supportedExportFormats: string[] = ['docx', 'pdf', 'rtf', 'txt'];
  
  // Import/export workers
  private docxWorker: Worker | null = null;
  private pdfWorker: Worker | null = null;
  
  // Configuration
  private maxFileSize: number = 50 * 1024 * 1024; // 50MB
  private enableProgressTracking: boolean = true;
  private preserveOriginalFormatting: boolean = true;

  constructor() {
    this.initializeWorkers();
  }

  // ============================================================================
  // DOCUMENT IMPORT
  // ============================================================================

  /**
   * Import document from file with format detection and conversion
   */
  async importDocument(
    file: File,
    options: Partial<ImportOptions> = {}
  ): Promise<ImportResult> {
    console.log(`[DocumentImportExportManager] Importing ${file.name} (${(file.size / 1024).toFixed(1)}KB)`);
    
    // Validate file
    const validation = this.validateImportFile(file);
    if (!validation.valid) {
      return {
        success: false,
        documentState: this.createEmptyDocumentState(),
        warnings: validation.warnings,
        errors: validation.errors,
        metadata: this.createEmptyMetadata(),
        preservedElements: [],
        lostElements: []
      };
    }
    
    // Set default options
    const importOptions: ImportOptions = {
      preserveFormatting: true,
      extractImages: true,
      preserveComments: true,
      preserveTrackChanges: true,
      convertToEditable: true,
      validateLegalContent: true,
      ...options
    };
    
    // Detect file format
    const format = this.detectFileFormat(file);
    
    // Import based on format
    switch (format) {
      case 'docx':
        return await this.importDOCX(file, importOptions);
      case 'pdf':
        return await this.importPDF(file, importOptions);
      case 'rtf':
        return await this.importRTF(file, importOptions);
      case 'txt':
        return await this.importTXT(file, importOptions);
      case 'html':
        return await this.importHTML(file, importOptions);
      default:
        return this.createFailedImportResult(`Unsupported format: ${format}`);
    }
  }

  /**
   * Import DOCX document with full formatting preservation
   */
  private async importDOCX(file: File, options: ImportOptions): Promise<ImportResult> {
    try {
      console.log('[DocumentImportExportManager] Importing DOCX document...');
      
      // Read file as array buffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Parse DOCX using worker
      const parseResult = await this.parseDOCXWithWorker(arrayBuffer, options);
      
      if (!parseResult.success) {
        return this.createFailedImportResult(parseResult.error || 'DOCX parsing failed');
      }
      
      // Convert to local document state
      const documentState = await this.convertToDocumentState(parseResult.content, options);
      
      // Extract metadata
      const metadata = this.extractDOCXMetadata(parseResult.metadata);
      
      // Validate legal content if requested
      const warnings: string[] = [];
      if (options.validateLegalContent) {
        const legalWarnings = await this.validateLegalContent(documentState);
        warnings.push(...legalWarnings);
      }
      
      return {
        success: true,
        documentState,
        warnings,
        errors: [],
        metadata,
        preservedElements: parseResult.preservedElements,
        lostElements: parseResult.lostElements
      };
      
    } catch (error) {
      console.error('[DocumentImportExportManager] DOCX import failed:', error);
      return this.createFailedImportResult(`DOCX import failed: ${error}`);
    }
  }

  /**
   * Import RTF document
   */
  private async importRTF(file: File, options: ImportOptions): Promise<ImportResult> {
    try {
      const text = await file.text();
      const documentState = await this.convertPlainTextToDocumentState(text);
      
      return {
        success: true,
        documentState,
        warnings: ['RTF import may not preserve all formatting'],
        errors: [],
        metadata: this.createEmptyMetadata(),
        preservedElements: ['text'],
        lostElements: ['formatting', 'images']
      };
    } catch (error) {
      return this.createFailedImportResult(`RTF import failed: ${error}`);
    }
  }

  /**
   * Import TXT document
   */
  private async importTXT(file: File, options: ImportOptions): Promise<ImportResult> {
    try {
      const text = await file.text();
      const documentState = await this.convertPlainTextToDocumentState(text);
      
      return {
        success: true,
        documentState,
        warnings: [],
        errors: [],
        metadata: this.createEmptyMetadata(),
        preservedElements: ['text'],
        lostElements: ['formatting']
      };
    } catch (error) {
      return this.createFailedImportResult(`TXT import failed: ${error}`);
    }
  }

  /**
   * Import HTML document
   */
  private async importHTML(file: File, options: ImportOptions): Promise<ImportResult> {
    try {
      const html = await file.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const text = doc.body?.textContent || '';
      
      const documentState = await this.convertPlainTextToDocumentState(text);
      
      return {
        success: true,
        documentState,
        warnings: ['HTML import may not preserve all formatting'],
        errors: [],
        metadata: this.createEmptyMetadata(),
        preservedElements: ['text', 'basic_structure'],
        lostElements: ['css_styles', 'images']
      };
    } catch (error) {
      return this.createFailedImportResult(`HTML import failed: ${error}`);
    }
  }

  /**
   * Import PDF document with text extraction
   */
  private async importPDF(file: File, options: ImportOptions): Promise<ImportResult> {
    try {
      console.log('[DocumentImportExportManager] Importing PDF document...');
      
      // Read file as array buffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Extract text using PDF worker
      const extractResult = await this.extractPDFWithWorker(arrayBuffer, options);
      
      if (!extractResult.success) {
        return this.createFailedImportResult(extractResult.error || 'PDF extraction failed');
      }
      
      // Convert to document state
      const documentState = await this.convertPlainTextToDocumentState(
        extractResult.text || '',
        extractResult.formatting
      );
      
      // Extract metadata
      const metadata = this.extractPDFMetadata(extractResult.metadata);
      
      const warnings = [
        'PDF import may not preserve all formatting',
        'Complex layouts may require manual adjustment'
      ];
      
      if (options.validateLegalContent) {
        const legalWarnings = await this.validateLegalContent(documentState);
        warnings.push(...legalWarnings);
      }
      
      return {
        success: true,
        documentState,
        warnings,
        errors: [],
        metadata,
        preservedElements: ['text', 'basic_formatting'],
        lostElements: ['images', 'complex_layouts', 'annotations']
      };
      
    } catch (error) {
      console.error('[DocumentImportExportManager] PDF import failed:', error);
      return this.createFailedImportResult(`PDF import failed: ${error}`);
    }
  }

  // ============================================================================
  // DOCUMENT EXPORT
  // ============================================================================

  /**
   * Export document to specified format with professional quality
   */
  async exportDocument(
    documentState: LocalDocumentState,
    options: ExportOptions
  ): Promise<ExportResult> {
    console.log(`[DocumentImportExportManager] Exporting to ${options.format.toUpperCase()}...`);
    
    const startTime = performance.now();
    
    try {
      let result: ExportResult;
      
      switch (options.format) {
        case 'docx':
          result = await this.exportToDOCX(documentState, options);
          break;
        case 'pdf':
          result = await this.exportToPDF(documentState, options);
          break;
        case 'rtf':
          result = await this.exportToRTF(documentState, options);
          break;
        case 'txt':
          result = await this.exportToTXT(documentState, options);
          break;
        default:
          throw new Error(`Unsupported export format: ${options.format}`);
      }
      
      result.exportTime = performance.now() - startTime;
      
      console.log(`[DocumentImportExportManager] Export completed in ${result.exportTime.toFixed(1)}ms (${(result.fileSize / 1024).toFixed(1)}KB)`);
      
      return result;
      
    } catch (error) {
      console.error('[DocumentImportExportManager] Export failed:', error);
      return {
        success: false,
        blob: new Blob(),
        filename: '',
        warnings: [],
        errors: [`Export failed: ${error}`],
        fileSize: 0,
        exportTime: performance.now() - startTime
      };
    }
  }

  /**
   * Export to DOCX with full formatting preservation
   */
  private async exportToDOCX(
    documentState: LocalDocumentState,
    options: ExportOptions
  ): Promise<ExportResult> {
    const docxOptions = options.docxOptions || {
      compatibility: 'office365',
      includeStyles: true,
      includeHeaders: true,
      includeFooters: true,
      includePageNumbers: true,
      protectDocument: false,
      trackChanges: false
    };
    
    // Generate DOCX using worker
    const generateResult = await this.generateDOCXWithWorker(documentState, docxOptions);
    
    if (!generateResult.success) {
      throw new Error(generateResult.error);
    }
    
    const filename = this.generateFilename(documentState, 'docx');
    
    return {
      success: true,
      blob: generateResult.blob,
      filename,
      warnings: generateResult.warnings || [],
      errors: [],
      fileSize: generateResult.blob.size,
      exportTime: 0 // Set by caller
    };
  }

  /**
   * Export to PDF with professional layout
   */
  private async exportToPDF(
    documentState: LocalDocumentState,
    options: ExportOptions
  ): Promise<ExportResult> {
    const pdfOptions = options.pdfOptions || {
      paperSize: 'letter',
      orientation: 'portrait',
      margins: { top: 1, bottom: 1, left: 1.5, right: 1 },
      includeBookmarks: true,
      protectDocument: false,
      allowPrinting: true,
      allowCopying: true
    };
    
    // Generate PDF using worker
    const generateResult = await this.generatePDFWithWorker(documentState, pdfOptions);
    
    if (!generateResult.success) {
      throw new Error(generateResult.error);
    }
    
    const filename = this.generateFilename(documentState, 'pdf');
    
    return {
      success: true,
      blob: generateResult.blob,
      filename,
      warnings: generateResult.warnings || [],
      errors: [],
      fileSize: generateResult.blob.size,
      exportTime: 0 // Set by caller
    };
  }

  /**
   * Export to RTF with formatting preservation
   */
  private async exportToRTF(
    documentState: LocalDocumentState,
    options: ExportOptions
  ): Promise<ExportResult> {
    // Generate RTF content
    const rtfContent = this.generateRTFContent(documentState, options);
    
    const blob = new Blob([rtfContent], { type: 'application/rtf' });
    const filename = this.generateFilename(documentState, 'rtf');
    
    return {
      success: true,
      blob,
      filename,
      warnings: ['RTF format may not preserve all advanced formatting'],
      errors: [],
      fileSize: blob.size,
      exportTime: 0
    };
  }

  /**
   * Export to plain text
   */
  private async exportToTXT(
    documentState: LocalDocumentState,
    options: ExportOptions
  ): Promise<ExportResult> {
    const textContent = documentState.text;
    
    const blob = new Blob([textContent], { type: 'text/plain' });
    const filename = this.generateFilename(documentState, 'txt');
    
    return {
      success: true,
      blob,
      filename,
      warnings: ['Text export removes all formatting'],
      errors: [],
      fileSize: blob.size,
      exportTime: 0
    };
  }

  // ============================================================================
  // WORKER INTEGRATION
  // ============================================================================

  private initializeWorkers(): void {
    try {
      // Initialize DOCX worker for import/export
      this.docxWorker = new Worker('/workers/docx-worker.js');
      
      // Initialize PDF worker for import/export
      this.pdfWorker = new Worker('/workers/pdf-worker.js');
      
      console.log('[DocumentImportExportManager] Workers initialized');
      
    } catch (error) {
      console.warn('[DocumentImportExportManager] Workers not available, using fallback methods');
    }
  }

  private async parseDOCXWithWorker(
    arrayBuffer: ArrayBuffer,
    options: ImportOptions
  ): Promise<{
    success: boolean;
    content?: any;
    metadata?: any;
    preservedElements: string[];
    lostElements: string[];
    error?: string;
  }> {
    if (!this.docxWorker) {
      return await this.parseDOCXFallback(arrayBuffer, options);
    }
    
    return new Promise((resolve) => {
      const messageId = Date.now().toString();
      
      const handleMessage = (event: MessageEvent) => {
        if (event.data.messageId === messageId) {
          this.docxWorker!.removeEventListener('message', handleMessage);
          resolve(event.data.result);
        }
      };
      
      this.docxWorker?.addEventListener('message', handleMessage);
      
      this.docxWorker?.postMessage({
        messageId,
        action: 'parse',
        arrayBuffer,
        options
      });
    });
  }

  private async extractPDFWithWorker(
    arrayBuffer: ArrayBuffer,
    options: ImportOptions
  ): Promise<{
    success: boolean;
    text?: string;
    formatting?: any;
    metadata?: any;
    error?: string;
  }> {
    if (!this.pdfWorker) {
      return await this.extractPDFFallback(arrayBuffer, options);
    }
    
    return new Promise((resolve) => {
      const messageId = Date.now().toString();
      
      const handleMessage = (event: MessageEvent) => {
        if (event.data.messageId === messageId) {
          this.pdfWorker!.removeEventListener('message', handleMessage);
          resolve(event.data.result);
        }
      };
      
      this.pdfWorker?.addEventListener('message', handleMessage);
      
      this.pdfWorker?.postMessage({
        messageId,
        action: 'extract',
        arrayBuffer,
        options
      });
    });
  }

  private async generateDOCXWithWorker(
    documentState: LocalDocumentState,
    options: DOCXExportOptions
  ): Promise<{
    success: boolean;
    blob: Blob;
    warnings?: string[];
    error?: string;
  }> {
    if (!this.docxWorker) {
      return await this.generateDOCXFallback(documentState, options);
    }
    
    return new Promise((resolve) => {
      const messageId = Date.now().toString();
      
      const handleMessage = (event: MessageEvent) => {
        if (event.data.messageId === messageId) {
          this.docxWorker!.removeEventListener('message', handleMessage);
          resolve(event.data.result);
        }
      };
      
      this.docxWorker?.addEventListener('message', handleMessage);
      
      this.docxWorker?.postMessage({
        messageId,
        action: 'generate',
        documentState,
        options
      });
    });
  }

  private async generatePDFWithWorker(
    documentState: LocalDocumentState,
    options: PDFExportOptions
  ): Promise<{
    success: boolean;
    blob: Blob;
    warnings?: string[];
    error?: string;
  }> {
    if (!this.pdfWorker) {
      return await this.generatePDFFallback(documentState, options);
    }
    
    return new Promise((resolve) => {
      const messageId = Date.now().toString();
      
      const handleMessage = (event: MessageEvent) => {
        if (event.data.messageId === messageId) {
          this.pdfWorker!.removeEventListener('message', handleMessage);
          resolve(event.data.result);
        }
      };
      
      this.pdfWorker?.addEventListener('message', handleMessage);
      
      this.pdfWorker?.postMessage({
        messageId,
        action: 'generate',
        documentState,
        options
      });
    });
  }

  // ============================================================================
  // FALLBACK IMPLEMENTATIONS
  // ============================================================================

  private async parseDOCXFallback(
    arrayBuffer: ArrayBuffer,
    options: ImportOptions
  ): Promise<{
    success: boolean;
    content?: any;
    metadata?: any;
    preservedElements: string[];
    lostElements: string[];
    error?: string;
  }> {
    console.log('[DocumentImportExportManager] Using DOCX fallback parser...');
    
    try {
      // Basic DOCX parsing using JSZip and XML parsing
      const JSZip = await import('jszip');
      const zip = await JSZip.default.loadAsync(arrayBuffer);
      
      // Extract document.xml
      const documentXml = await zip.file('word/document.xml')?.async('text');
      if (!documentXml) {
        throw new Error('Invalid DOCX file: missing document.xml');
      }
      
      // Parse XML to extract text
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(documentXml, 'text/xml');
      
      // Extract text content
      const textNodes = xmlDoc.querySelectorAll('w\\:t, t');
      const extractedText = Array.from(textNodes)
        .map(node => node.textContent || '')
        .join('');
      
      // Extract basic metadata
      const metadata = await this.extractDOCXMetadataFallback(zip);
      
      return {
        success: true,
        content: { text: extractedText },
        metadata,
        preservedElements: ['text', 'basic_structure'],
        lostElements: ['images', 'complex_formatting', 'tables', 'headers', 'footers']
      };
      
    } catch (error) {
      return {
        success: false,
        preservedElements: [],
        lostElements: [],
        error: `DOCX fallback failed: ${error}`
      };
    }
  }

  private async extractPDFFallback(
    arrayBuffer: ArrayBuffer,
    options: ImportOptions
  ): Promise<{
    success: boolean;
    text?: string;
    formatting?: any;
    metadata?: any;
    error?: string;
  }> {
    console.log('[DocumentImportExportManager] Using PDF fallback extractor...');
    
    try {
      // Basic PDF text extraction using pdf-lib or pdfjs-dist
      const pdfjsLib = await import('pdfjs-dist');
      
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let extractedText = '';
      
      // Extract text from all pages
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        
        extractedText += pageText + '\n\n';
      }
      
      return {
        success: true,
        text: extractedText.trim(),
        formatting: null,
        metadata: { pageCount: pdf.numPages }
      };
      
    } catch (error) {
      return {
        success: false,
        error: `PDF fallback failed: ${error}`
      };
    }
  }

  private async generateDOCXFallback(
    documentState: LocalDocumentState,
    options: DOCXExportOptions
  ): Promise<{
    success: boolean;
    blob: Blob;
    warnings?: string[];
    error?: string;
  }> {
    console.log('[DocumentImportExportManager] Using DOCX fallback generator...');
    
    try {
      // Basic DOCX generation using docx library
      const docx = await import('docx');
      
      // Create document
      const doc = new docx.Document({
        sections: [{
          properties: {},
          children: [
            new docx.Paragraph({
              children: [
                new docx.TextRun({
                  text: documentState.text,
                  font: 'Times New Roman',
                  size: 24 // 12pt = 24 half-points
                })
              ]
            })
          ]
        }]
      });
      
      // Generate blob
      const buffer = await docx.Packer.toBuffer(doc);
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      
      return {
        success: true,
        blob,
        warnings: ['Fallback DOCX generation may not preserve all formatting']
      };
      
    } catch (error) {
      return {
        success: false,
        blob: new Blob(),
        error: `DOCX fallback failed: ${error}`
      };
    }
  }

  private async generatePDFFallback(
    documentState: LocalDocumentState,
    options: PDFExportOptions
  ): Promise<{
    success: boolean;
    blob: Blob;
    warnings?: string[];
    error?: string;
  }> {
    console.log('[DocumentImportExportManager] Using PDF fallback generator...');
    
    try {
      // Basic PDF generation using jsPDF
      const jsPDF = await import('jspdf');
      const pdf = new jsPDF.default();
      
      // Add text content
      const lines = documentState.text.split('\n');
      let yPosition = 20;
      
      for (const line of lines) {
        if (yPosition > 280) { // Near bottom of page
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.text(line, 20, yPosition);
        yPosition += 6; // Line spacing
      }
      
      // Generate blob
      const pdfBlob = pdf.output('blob');
      
      return {
        success: true,
        blob: pdfBlob,
        warnings: ['Fallback PDF generation may not preserve complex formatting']
      };
      
    } catch (error) {
      return {
        success: false,
        blob: new Blob(),
        error: `PDF fallback failed: ${error}`
      };
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private validateImportFile(file: File): {
    valid: boolean;
    warnings: string[];
    errors: string[];
  } {
    const warnings: string[] = [];
    const errors: string[] = [];
    
    // Check file size
    if (file.size > this.maxFileSize) {
      errors.push(`File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB (max: ${this.maxFileSize / 1024 / 1024}MB)`);
    }
    
    // Check file type
    const format = this.detectFileFormat(file);
    if (!this.supportedImportFormats.includes(format)) {
      errors.push(`Unsupported file format: ${format}`);
    }
    
    // File size warnings
    if (file.size > 10 * 1024 * 1024) { // 10MB
      warnings.push('Large file may take longer to import');
    }
    
    return {
      valid: errors.length === 0,
      warnings,
      errors
    };
  }

  private detectFileFormat(file: File): string {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    
    // Map extensions to formats
    const formatMap: { [ext: string]: string } = {
      'docx': 'docx',
      'doc': 'doc',
      'pdf': 'pdf',
      'rtf': 'rtf',
      'txt': 'txt',
      'html': 'html',
      'htm': 'html'
    };
    
    return formatMap[extension] || 'unknown';
  }

  private async convertToDocumentState(
    content: any,
    _options: ImportOptions
  ): Promise<LocalDocumentState> {
    // Convert parsed content to local document state
    const text = content.text || '';
    
    return {
      text,
      caretPosition: 0,
      selection: null,
      version: 1,
      isDirty: false,
      created: new Date(),
      modified: new Date(),
      attorney: this.getDefaultAttorney(),
      documentType: 'imported',
      jurisdiction: 'federal',
      lastServerSync: 0,
      serverVersion: 0,
      dirtyRegions: new Set()
    };
  }

  private async convertPlainTextToDocumentState(
    text: string,
    _formatting?: any
  ): Promise<LocalDocumentState> {
    return {
      text,
      caretPosition: 0,
      selection: null,
      version: 1,
      isDirty: false,
      created: new Date(),
      modified: new Date(),
      attorney: this.getDefaultAttorney(),
      documentType: 'imported',
      jurisdiction: 'federal',
      lastServerSync: 0,
      serverVersion: 0,
      dirtyRegions: new Set()
    };
  }

  private generateFilename(documentState: LocalDocumentState, format: string): string {
    const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const docType = documentState.documentType || 'document';
    
    return `${docType}_${timestamp}.${format}`;
  }

  private generateRTFContent(documentState: LocalDocumentState, options: ExportOptions): string {
    // Basic RTF generation
    const rtfHeader = '{\\rtf1\\ansi\\deff0 {\\fonttbl {\\f0 Times New Roman;}}';
    const rtfContent = documentState.text
      .replace(/\n/g, '\\par ')
      .replace(/[{}\\]/g, '\\$&');
    const rtfFooter = '}';
    
    return rtfHeader + rtfContent + rtfFooter;
  }

  private async validateLegalContent(documentState: LocalDocumentState): Promise<string[]> {
    const warnings: string[] = [];
    
    // Check for common legal document elements
    const text = documentState.text.toLowerCase();
    
    if (!text.includes('attorney') && !text.includes('counsel')) {
      warnings.push('Document may be missing attorney identification');
    }
    
    if (!text.includes('jurisdiction') && !text.includes('court')) {
      warnings.push('Document may be missing jurisdiction or court information');
    }
    
    if (text.length < 100) {
      warnings.push('Document appears unusually short for legal content');
    }
    
    return warnings;
  }

  // ============================================================================
  // METADATA EXTRACTION
  // ============================================================================

  private extractDOCXMetadata(metadata: any): DocumentMetadata {
    return {
      originalFormat: 'docx',
      author: metadata?.author || 'Unknown',
      created: metadata?.created ? new Date(metadata.created) : new Date(),
      modified: metadata?.modified ? new Date(metadata.modified) : new Date(),
      title: metadata?.title,
      subject: metadata?.subject,
      keywords: metadata?.keywords ? metadata.keywords.split(',') : [],
      pageCount: metadata?.pageCount || 1,
      wordCount: metadata?.wordCount || 0,
      characterCount: metadata?.characterCount || 0
    };
  }

  private extractPDFMetadata(metadata: any): DocumentMetadata {
    return {
      originalFormat: 'pdf',
      author: metadata?.author || 'Unknown',
      created: metadata?.created ? new Date(metadata.created) : new Date(),
      modified: metadata?.modified ? new Date(metadata.modified) : new Date(),
      title: metadata?.title,
      subject: metadata?.subject,
      pageCount: metadata?.pageCount || 1,
      wordCount: 0, // Will be calculated from extracted text
      characterCount: 0
    };
  }

  private async extractDOCXMetadataFallback(zip: any): Promise<any> {
    try {
      // Extract core.xml for metadata
      const coreXml = await zip.file('docProps/core.xml')?.async('text');
      if (coreXml) {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(coreXml, 'text/xml');
        
        return {
          author: xmlDoc.querySelector('creator')?.textContent,
          title: xmlDoc.querySelector('title')?.textContent,
          subject: xmlDoc.querySelector('subject')?.textContent,
          created: xmlDoc.querySelector('created')?.textContent,
          modified: xmlDoc.querySelector('modified')?.textContent
        };
      }
    } catch (error) {
      console.warn('[DocumentImportExportManager] Metadata extraction failed:', error);
    }
    
    return {};
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private createEmptyDocumentState(): LocalDocumentState {
    return {
      text: '',
      caretPosition: 0,
      selection: null,
      version: 1,
      isDirty: false,
      created: new Date(),
      modified: new Date(),
      attorney: this.getDefaultAttorney(),
      documentType: 'document',
      jurisdiction: 'federal',
      lastServerSync: 0,
      serverVersion: 0,
      dirtyRegions: new Set()
    };
  }

  private createEmptyMetadata(): DocumentMetadata {
    return {
      originalFormat: 'unknown',
      author: 'Unknown',
      created: new Date(),
      modified: new Date(),
      pageCount: 0,
      wordCount: 0,
      characterCount: 0
    };
  }

  private createFailedImportResult(error: string): ImportResult {
    return {
      success: false,
      documentState: this.createEmptyDocumentState(),
      warnings: [],
      errors: [error],
      metadata: this.createEmptyMetadata(),
      preservedElements: [],
      lostElements: []
    };
  }

  private getDefaultAttorney(): AttorneyInfo {
    return {
      id: 'default',
      name: 'Unknown Attorney',
      email: '',
      firmName: '',
      firm: '',
      barNumber: '',
      role: 'associate',
      seniority: 0,
      jurisdiction: 'federal',
      title: 'Attorney',
      phone: '',
      address: ''
    };
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get supported import formats
   */
  getSupportedImportFormats(): string[] {
    return [...this.supportedImportFormats];
  }

  /**
   * Get supported export formats
   */
  getSupportedExportFormats(): string[] {
    return [...this.supportedExportFormats];
  }

  /**
   * Check if format is supported for import
   */
  isImportFormatSupported(format: string): boolean {
    return this.supportedImportFormats.includes(format.toLowerCase());
  }

  /**
   * Check if format is supported for export
   */
  isExportFormatSupported(format: string): boolean {
    return this.supportedExportFormats.includes(format.toLowerCase());
  }

  /**
   * Get default export options for format
   */
  getDefaultExportOptions(format: string): Partial<ExportOptions> {
    switch (format.toLowerCase()) {
      case 'docx':
        return {
          format: 'docx',
          includeMetadata: true,
          preserveFormatting: true,
          includeTrackChanges: false,
          includeComments: false,
          docxOptions: {
            compatibility: 'office365',
            includeStyles: true,
            includeHeaders: true,
            includeFooters: true,
            includePageNumbers: true,
            protectDocument: false,
            trackChanges: false
          }
        };
        
      case 'pdf':
        return {
          format: 'pdf',
          includeMetadata: true,
          preserveFormatting: true,
          pdfOptions: {
            paperSize: 'letter',
            orientation: 'portrait',
            margins: { top: 1, bottom: 1, left: 1.5, right: 1 },
            includeBookmarks: false,
            protectDocument: false,
            allowPrinting: true,
            allowCopying: true
          }
        };
        
      default:
        return {
          format: format as any,
          includeMetadata: true,
          preserveFormatting: true
        };
    }
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Destroy import/export manager
   */
  destroy(): void {
    if (this.docxWorker) {
      this.docxWorker.terminate();
    }
    
    if (this.pdfWorker) {
      this.pdfWorker.terminate();
    }
    
    console.log('[DocumentImportExportManager] Destroyed');
  }
}
